import {
    type ReactNode,
    useState,
    useMemo,
    useRef
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    type IMenuRef,
    Button,
    Switch,
    Menu
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    HomeIcon,
    FileIcon,
    UserIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Menu";

const USAGE_CODE = `const menuRef = useRef<IMenuRef>(null);

<Button
    onPress={() => menuRef.current?.open()}
    title="Open menu"
/>
<Menu
    buttons={[
        {
            redirectMain: "Home",
            icon: HomeIcon,
            title: "Home"
        },
        {
            redirectMain: "Library",
            icon: FileIcon,
            title: "Library"
        }
    ]}
    navigation={navigation}
    isAutoClosed={true}
    id="app-menu"
    ref={menuRef}
/>`;

const GROUP_CODE = `{
    subButtons: [
        {
            redirectMain: "Library",
            redirectSub: "Documents",
            title: "Documents"
        }
    ],
    isCollapsible: true,
    isCollapse: true,
    title: "Library",
    icon: FileIcon
}`;

const CONTEXT_CODE = `NCoreUIKitMenu.load({
    isAutoClosed: true,
    id: "app-menu",
    navigation,
    buttons
});

NCoreUIKitMenu.open({
    id: "app-menu"
});`;

const INITIAL_ROUTE = "home";

const stylesheet = StyleSheet.create({
    stage: {
        justifyContent: "flex-end",
        alignItems: "center",
        flexDirection: "row",
        position: "relative",
        height: "100%",
        width: "100%"
    }
});

const STAGE_MENU_PROPS = {
    isWorkWithPortal: false,
    isWorkWithModal: false
} as const;

const createDemoNavigation = (route: string, onNavigate: (route: string) => void): NCoreUIKit.Navigation => {
    const state: NCoreUIKit.NavigationState = {
        routes: [
            {
                name: route,
                key: route
            }
        ],
        index: 0
    };

    return {
        navigate: (page, config) => onNavigate(config?.screen ?? page),
        addListener: () => () => undefined,
        getRootState: () => state,
        goBack: () => undefined,
        canGoBack: () => false,
        getState: () => state
    };
};

const Stage = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <DemoFrame
        height={shapeConfigs.chartHeight.large}
    >
        <View
            style={[
                stylesheet.stage,
                {
                    padding: spaces.spacingLg
                }
            ]}
        >
            {children}
        </View>
    </DemoFrame>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("menuPage.buttonsTitle"),
        id: "buttons"
    },
    {
        title: t("menuPage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("menuPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("menuPage.behaviourTitle"),
        id: "behaviour"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MenuPage = () => {
    const {
        t
    } = useDocsI18n();

    const overviewRef = useRef<IMenuRef>(null);

    const buttonsRef = useRef<IMenuRef>(null);

    const groupsRef = useRef<IMenuRef>(null);

    const layoutRef = useRef<IMenuRef>(null);

    const [
        route,
        setRoute
    ] = useState(INITIAL_ROUTE);

    const [
        isCloseVisible,
        setIsCloseVisible
    ] = useState(true);

    const [
        isControlledOpen,
        setIsControlledOpen
    ] = useState(false);

    const [
        lastEvent,
        setLastEvent
    ] = useState("—");

    const navigation = useMemo(() => createDemoNavigation(route, setRoute), [
        route
    ]);

    const routeTitles: Record<string, string> = {
        favourites: t("menuPage.favourites"),
        documents: t("menuPage.documents"),
        library: t("sample.library"),
        profile: t("sample.item1"),
        home: t("sample.home")
    };

    const handleControlledOpen = () => setIsControlledOpen(true);

    const handleControlledClose = () => {
        setIsControlledOpen(false);

        setLastEvent("onClose");
    };

    const renderFooter = () => <Button
        title={t("sample.item3")}
        onPress={() => undefined}
        variant="outline"
        type="neutral"
    />;

    return <Article>
        <ComponentHeader
            lead={t("menuPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("menuPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("menuPage.overviewCaption", [
                    routeTitles[route] ?? route
                ])}
            >
                <Stage>
                    <Button
                        onPress={() => overviewRef.current?.open()}
                        title={t("menuPage.open")}
                        spreadBehaviour="free"
                    />
                    <Menu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            },
                            {
                                title: t("sample.item1"),
                                redirectMain: "profile",
                                icon: UserIcon
                            }
                        ]}
                        {...STAGE_MENU_PROPS}
                        id="docs-menu-overview"
                        navigation={navigation}
                        isAutoClosed={true}
                        ref={overviewRef}
                    />
                </Stage>
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("menuPage.stageNote")}
            </Paragraph>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("menuPage.buttonsTitle")}
            id="buttons"
        >
            <Paragraph>
                {t("menuPage.buttonsText")}
            </Paragraph>
            <Preview>
                <Stage>
                    <Button
                        onPress={() => buttonsRef.current?.open()}
                        title={t("menuPage.open")}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                    />
                    <Menu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                props: {
                                    title: t("sample.library"),
                                    rightTitle: "3"
                                },
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            },
                            {
                                isShowRedirectIcon: true,
                                title: t("sample.item1"),
                                redirectMain: "profile",
                                icon: UserIcon
                            },
                            {
                                props: {
                                    rightTitle: t("menuPage.soon"),
                                    title: t("menuPage.templates"),
                                    isDisabled: true
                                },
                                title: t("menuPage.templates"),
                                icon: StarIcon
                            }
                        ]}
                        {...STAGE_MENU_PROPS}
                        navigation={navigation}
                        id="docs-menu-buttons"
                        isAutoClosed={true}
                        ref={buttonsRef}
                    />
                </Stage>
            </Preview>
        </Section>
        <Section
            title={t("menuPage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("menuPage.groupsText")}
            </Paragraph>
            <Preview>
                <Stage>
                    <Button
                        onPress={() => groupsRef.current?.open()}
                        title={t("menuPage.open")}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                    />
                    <Menu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                subButtons: [
                                    {
                                        title: t("menuPage.documents"),
                                        redirectMain: "library",
                                        redirectSub: "documents"
                                    },
                                    {
                                        title: t("menuPage.favourites"),
                                        redirectSub: "favourites",
                                        redirectMain: "library"
                                    }
                                ],
                                title: t("sample.library"),
                                isCollapsible: true,
                                isCollapse: true,
                                icon: FileIcon
                            },
                            {
                                title: t("sample.item1"),
                                redirectMain: "profile",
                                icon: UserIcon
                            }
                        ]}
                        {...STAGE_MENU_PROPS}
                        navigation={navigation}
                        id="docs-menu-groups"
                        isAutoClosed={true}
                        ref={groupsRef}
                    />
                </Stage>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("menuPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("menuPage.layoutText")}
            </Paragraph>
            <Preview>
                <Switch
                    onCheckedChange={setIsCloseVisible}
                    title="isShowCollapseButton"
                    isChecked={isCloseVisible}
                />
                <Stage>
                    <Button
                        onPress={() => layoutRef.current?.open()}
                        title={t("menuPage.open")}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                    />
                    <Menu
                        isShowCollapseButton={isCloseVisible}
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            }
                        ]}
                        siteLogoProps={{
                            subTitle: t("sample.subtitle"),
                            title: t("sample.brand")
                        }}
                        {...STAGE_MENU_PROPS}
                        renderFooter={renderFooter}
                        navigation={navigation}
                        id="docs-menu-layout"
                        ref={layoutRef}
                    />
                </Stage>
            </Preview>
        </Section>
        <Section
            title={t("menuPage.behaviourTitle")}
            id="behaviour"
        >
            <Paragraph>
                {t("menuPage.behaviourText")}
            </Paragraph>
            <Preview
                caption={t("menuPage.behaviourCaption", [
                    lastEvent
                ])}
            >
                <Stage>
                    <Button
                        onPress={handleControlledOpen}
                        title={t("menuPage.open")}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                    />
                    <Menu
                        onOpened={() => setLastEvent("onOpened")}
                        onClosed={() => setLastEvent("onClosed")}
                        onOpen={() => setLastEvent("onOpen")}
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            }
                        ]}
                        onClose={handleControlledClose}
                        isCollapse={isControlledOpen}
                        {...STAGE_MENU_PROPS}
                        id="docs-menu-behaviour"
                        navigation={navigation}
                        isAutoClosed={true}
                    />
                </Stage>
            </Preview>
            <Paragraph>
                {t("menuPage.contextText")}
            </Paragraph>
            <CodeBlock
                code={CONTEXT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("menuPage.a11yLandmark"),
                    t("menuPage.a11yDialog"),
                    t("menuPage.a11yPanel"),
                    t("menuPage.a11yCurrent"),
                    t("menuPage.a11yKeyboard"),
                    t("menuPage.a11yGroup"),
                    t("menuPage.a11yClose"),
                    t("menuPage.a11yMotion"),
                    t("menuPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("menuPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("menuPage.propButtons"),
                        type: "Array<MenuButton>",
                        name: "buttons"
                    },
                    {
                        description: t("menuPage.propNavigation"),
                        type: "NCoreUIKit.Navigation",
                        name: "navigation"
                    },
                    {
                        description: t("menuPage.propIsCollapse"),
                        name: "isCollapse",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propIsAutoClosed"),
                        name: "isAutoClosed",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propIsAutoClosedOnClosed"),
                        name: "isAutoClosedOnClosed",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propClose"),
                        type: "() => void",
                        name: "close"
                    },
                    {
                        description: t("menuPage.propIsShowCollapseButton"),
                        name: "isShowCollapseButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propRenderSiteLogo"),
                        type: "() => ReactNode",
                        name: "renderSiteLogo"
                    },
                    {
                        description: t("menuPage.propSiteLogoProps"),
                        type: "ISiteLogoProps",
                        name: "siteLogoProps"
                    },
                    {
                        description: t("menuPage.propRenderHeader"),
                        type: "() => ReactNode",
                        name: "renderHeader"
                    },
                    {
                        description: t("menuPage.propRenderHeaderSeparator"),
                        name: "renderHeaderSeparator",
                        type: "() => ReactNode"
                    },
                    {
                        description: t("menuPage.propSeparatorProps"),
                        type: "ISeparatorProps",
                        name: "separatorProps"
                    },
                    {
                        description: t("menuPage.propRenderFooter"),
                        type: "() => ReactNode",
                        name: "renderFooter"
                    },
                    {
                        description: t("menuPage.propGlassEffect"),
                        name: "glassEffect",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        description: t("menuPage.propCustomBackgroundColor"),
                        name: "customBackgroundColor",
                        type: "string"
                    },
                    {
                        description: t("menuPage.propCustomBackgroundColorKey"),
                        type: "keyof ProjectColorPalette",
                        name: "customBackgroundColorKey"
                    },
                    {
                        description: t("menuPage.propIsWorkWithModal"),
                        name: "isWorkWithModal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propPortalName"),
                        defaultValue: "\"menu-system\"",
                        name: "portalName",
                        type: "string"
                    },
                    {
                        description: t("menuPage.propIsWorkWithSafeAreaView"),
                        name: "isWorkWithSafeAreaView",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propIsWorkWithAnimation"),
                        name: "isWorkWithAnimation",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propModalProps"),
                        type: "IModalProps",
                        name: "modalProps"
                    },
                    {
                        type: "(props: { id?: string }) => void",
                        description: t("menuPage.propOnOpen"),
                        name: "onOpen"
                    },
                    {
                        type: "(props: { id?: string }) => void",
                        description: t("menuPage.propOnOpened"),
                        name: "onOpened"
                    },
                    {
                        type: "(props: { id?: string }) => void",
                        description: t("menuPage.propOnClose"),
                        name: "onClose"
                    },
                    {
                        type: "(props: { id?: string }) => void",
                        description: t("menuPage.propOnClosed"),
                        name: "onClosed"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("menuPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("menuPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("menuPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("menuPage.reservedText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("menuPage.propIsCollapsible"),
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        type: "{ image?, imageUrl?, title? }",
                        description: t("menuPage.propLogo"),
                        name: "logo"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    buttons
                </InlineCode>
                {" "}
                {t("menuPage.partButtonText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("menuPage.propButtonTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("menuPage.propButtonIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("menuPage.propButtonRedirectMain"),
                        name: "redirectMain",
                        type: "string"
                    },
                    {
                        description: t("menuPage.propButtonRedirectSub"),
                        name: "redirectSub",
                        type: "string"
                    },
                    {
                        description: t("menuPage.propButtonSubButtons"),
                        type: "Array<MenuButton>",
                        name: "subButtons"
                    },
                    {
                        description: t("menuPage.propButtonIsCollapsible"),
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propButtonIsCollapse"),
                        name: "isCollapse",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propButtonIsShowRedirectIcon"),
                        type: "NCoreUIKitIcon | boolean",
                        name: "isShowRedirectIcon"
                    },
                    {
                        description: t("menuPage.propButtonIsUseLocalize"),
                        name: "isUseLocalize",
                        type: "boolean"
                    },
                    {
                        description: t("menuPage.propButtonProps"),
                        type: "IRowCardProps",
                        name: "props"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("menuPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MenuPage;
